import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ExitSnapshotView.css";
import { fetchSnapshotAtTime, fetchSnapshotMeta, fetchAvailableTimes, generateSnapshots, fetchDateList } from "../../services/exitSnapshotService";
import B10CEDetailView from "./B10CEDetailView";

// ─── Constants ─────────────────────────────────────────────────────────────

const INDICES = ["NIFTY 50", "SENSEX"];
const OPTION_TYPES = ["CE", "PE"];
const OPENING_TYPES = [
  { value: "SW", label: "Sideways" },
  { value: "GU", label: "Gap Up" },
  { value: "GD", label: "Gap Down" },
];
const OT_COLORS = { SW: "#388bfd", GU: "#d29922", GD: "#f85149" };

// ─── Utility helpers ───────────────────────────────────────────────────────

function formatValue(value) {
  if (value === null || value === undefined) return { text: "—", cls: "es-metric-value--null" };
  if (typeof value === "boolean") {
    return { text: value ? "TRUE" : "FALSE", cls: value ? "es-metric-value--true" : "es-metric-value--false" };
  }
  if (typeof value === "number") {
    const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);
    return { text: formatted, cls: "es-metric-value--number" };
  }
  if (typeof value === "string") return { text: value, cls: "es-metric-value--string" };
  return { text: JSON.stringify(value), cls: "es-metric-value--string" };
}

function todayIST() {
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return ist.toISOString().slice(0, 10);
}

// ─── Sub-components ────────────────────────────────────────────────────────

function MetricRow({ name, value }) {
  const { text, cls } = formatValue(value);
  const isTrue = value === true;
  return (
    <div className={`es-metric-row${isTrue ? " es-metric-row--true" : ""}`}>
      <span className="es-metric-key" title={name}>{name}</span>
      <span className={`es-metric-value ${cls}`}>{text}</span>
    </div>
  );
}

function MetricGroup({ name, fields }) {
  return (
    <div className="es-metric-group">
      <div className="es-metric-group__header">{name}</div>
      {Object.entries(fields).map(([key, val]) => (
        <MetricRow key={key} name={key} value={val} />
      ))}
    </div>
  );
}

function MatchedExitsList({ matchedExits }) {
  if (!matchedExits || matchedExits.length === 0) return null;
  return (
    <div className="es-matched-exits">
      <div className="es-matched-exits__title">Matched Exits ({matchedExits.length})</div>
      <div>
        {matchedExits.map((me, i) => (
          <div className="es-exit-chip" key={i} title={me.exitInfo || ""}>
            <span className="es-exit-chip__code">{me.exitCode || me.exitClassName}</span>
            {me.conditions && me.conditions.length > 0 && (
              <span className="es-exit-chip__conditions">
                {me.conditions.slice(0, 8).map((c, ci) => (
                  <span key={ci} className="es-exit-chip__cond-tag">{c}</span>
                ))}
                {me.conditions.length > 8 && (
                  <span className="es-exit-chip__cond-tag">+{me.conditions.length - 8}</span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExitClassList({ exitClasses, selectedClass, onSelect, isExpiry, openingType }) {
  const renderClassItem = (cls) => {
    const displayName = cls.className.startsWith("NEX_") ? cls.className.replace("NEX_", "") : cls.className;
    return (
      <div
        key={cls.className}
        className={[
          "es-class-item",
          cls.matched ? "es-class-item--matched" : "",
          selectedClass === cls.className ? "es-class-item--selected" : "",
        ].join(" ")}
        onClick={() => onSelect(cls.className)}
      >
        <span className={`es-class-badge ${cls.matched ? "es-class-badge--matched" : "es-class-badge--unmatched"}`}>
          {cls.matched ? "\u2713" : "\u2717"}
        </span>
        <span className="es-class-name">{displayName}</span>
        {cls.matchedExitCount > 0 && (
          <span className="es-class-count">{cls.matchedExitCount}</span>
        )}
      </div>
    );
  };

  const otInfo = OPENING_TYPES.find(o => o.value === openingType);

  return (
    <div className="es-class-list">
      <div className="es-class-list__header">
        {isExpiry ? "EXPIRY EXITS" : "NON-EXPIRY EXITS"}
      </div>
      {!isExpiry && otInfo && (
        <div className="es-opening-header" style={{ borderLeftColor: OT_COLORS[openingType] || "#8b949e" }}>
          <span className="es-opening-header__badge" style={{ background: OT_COLORS[openingType] || "#8b949e" }}>
            {openingType}
          </span>
          <span>{otInfo.label}</span>
        </div>
      )}
      <div
        className={`es-class-item${!selectedClass ? " es-class-item--selected" : ""}`}
        onClick={() => onSelect(null)}
      >
        <span className="es-class-badge es-class-badge--unmatched">&sum;</span>
        <span className="es-class-name" style={{ color: "#c9d1d9" }}>All Metrics</span>
      </div>
      {exitClasses.map(renderClassItem)}
    </div>
  );
}

function DetailHeader({ selectedClassData, summary, source }) {
  const label = selectedClassData ? selectedClassData.className : "All Exit Metrics";
  const matched = selectedClassData ? selectedClassData.matched : false;
  return (
    <div className="es-detail-header">
      <span className={`es-detail-header__code${matched ? " es-detail-header__code--matched" : ""}`}>
        {label}
      </span>
      {summary && (
        <>
          <span className="es-detail-header__stat">
            Matched: <span className="es-detail-header__stat-val">{summary.matchedCount}</span>
          </span>
          <span className="es-detail-header__stat">
            Classes: <span className="es-detail-header__stat-val">{summary.matchedClasses}/{summary.totalClasses}</span>
          </span>
          {summary.expiry && (
            <span className="es-detail-header__stat">
              Expiry: <span className="es-detail-header__stat-val">{summary.expiry}</span>
            </span>
          )}
          {summary.leftDay !== undefined && (
            <span className="es-detail-header__stat">
              Left: <span className="es-detail-header__stat-val">{summary.leftDay}d</span>
            </span>
          )}
          {summary.indexOpeningType && (
            <span className="es-detail-header__stat">
              Opening: <span className="es-detail-header__stat-val">{summary.indexOpeningType}</span>
            </span>
          )}
        </>
      )}
      {source && (
        <span className={`es-source-badge es-source-badge--${source}`}>
          {source === "exit_items" ? "from exit_items" : "from snapshots"}
        </span>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export default function ExitSnapshotView() {
  const [indexName, setIndexName]   = useState("NIFTY 50");
  const [optionType, setOptionType] = useState("CE");
  const [onlyDate, setOnlyDate]     = useState(todayIST());
  const [onlyTime, setOnlyTime]     = useState("09:20");
  const [expiry, setExpiry]         = useState(""); // eslint-disable-line

  // ── Expiry / Non-Expiry mode ──
  const [isExpiry, setIsExpiry]           = useState(true);
  const [openingType, setOpeningType]     = useState("");   // "SW" | "GU" | "GD"
  const [dateList, setDateList]           = useState([]);
  const [dateListLoading, setDateListLoading] = useState(false);

  const [meta, setMeta]                     = useState({ expiries: [], dates: [] });
  const [availableTimes, setAvailableTimes] = useState([]);
  const [snapshotData, setSnapshotData]     = useState(null);
  const [selectedClass, setSelectedClass]   = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [initialLoad, setInitialLoad]       = useState(true);

  const loadSnapshotRef = useRef(null);

  // ── Fetch snapshot (stable callback) ────────────────────────
  const doLoad = useCallback((idx, opt, date, time, expMode) => {
    if (!idx || !opt || !date || !time) return;
    setLoading(true);
    setError(null);
    fetchSnapshotAtTime({ indexName: idx, optionType: opt, onlyDate: date, onlyTime: time, isExpiry: expMode })
      .then((data) => {
        if (data?.error) {
          setSnapshotData(null);
          setError(data.error);
        } else {
          setSnapshotData(data);
          setError(null);
        }
      })
      .catch((e) => {
        setError(e?.response?.data || e.message || "Failed to load snapshot");
        setSnapshotData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Wrap for button click
  const loadSnapshot = useCallback(() => {
    doLoad(indexName, optionType, onlyDate, onlyTime, isExpiry);
  }, [doLoad, indexName, optionType, onlyDate, onlyTime, isExpiry]);

  // Save ref for use in effects
  loadSnapshotRef.current = loadSnapshot;

  // ── Fetch date list from daily_trades ──────────────────────
  useEffect(() => {
    if (!isExpiry && !openingType) {
      setDateList([]);
      return;
    }
    setDateListLoading(true);
    fetchDateList({ indexName, isExpiry, openingType: isExpiry ? undefined : openingType })
      .then((d) => {
        const dates = d?.dates || [];
        setDateList(dates);
        // Auto-select first date
        if (dates.length > 0 && !dates.includes(onlyDate)) {
          setOnlyDate(dates[0]);
        }
      })
      .catch(() => setDateList([]))
      .finally(() => setDateListLoading(false));
  }, [indexName, isExpiry, openingType]); // eslint-disable-line

  // ── Load meta on mount / filter change ──────────────────────
  useEffect(() => {
    setSnapshotData(null);
    setSelectedClass(null);
    fetchSnapshotMeta({ indexName, optionType })
      .then((m) => {
        setMeta(m || { expiries: [], dates: [] });
      })
      .catch(() => setMeta({ expiries: [], dates: [] }));
  }, [indexName, optionType]);

  // ── Load available times when date/index changes ─────────────
  useEffect(() => {
    if (!onlyDate) return;
    // For non-expiry: don't load times until opening type is selected
    if (!isExpiry && !openingType) return;

    fetchAvailableTimes({ indexName, optionType, onlyDate, expiry: expiry || undefined, isExpiry })
      .then((t) => {
        const times = t?.times || [];
        setAvailableTimes(times);
        // Auto-select first available time and auto-load
        if (times.length > 0) {
          const firstTime = times[0].slice(0, 5);
          setOnlyTime(firstTime);
          doLoad(indexName, optionType, onlyDate, firstTime, isExpiry);
        } else {
          setSnapshotData(null);
          setError(null);
        }
        setInitialLoad(false);
      })
      .catch(() => {
        setAvailableTimes([]);
        setInitialLoad(false);
      });
  }, [indexName, optionType, onlyDate, expiry, isExpiry, openingType, doLoad]);

  // ── Mode switch: clear state ────────────────────────────────
  useEffect(() => {
    setSnapshotData(null);
    setSelectedClass(null);
    setAvailableTimes([]);
    setError(null);
    if (!isExpiry) {
      setOpeningType(""); // force user to pick opening type
    }
  }, [isExpiry]);

  // ── Date step through dateList ──────────────────────────────
  const stepDate = useCallback((delta) => {
    if (dateList.length === 0) return;
    const idx = dateList.indexOf(onlyDate);
    const cur = idx >= 0 ? idx : 0;
    // dateList is sorted descending, so -1 = next newer, +1 = next older
    const next = Math.max(0, Math.min(dateList.length - 1, cur - delta));
    setOnlyDate(dateList[next]);
  }, [dateList, onlyDate]);

  // ── Time navigation helpers ───────────────────────────────────
  const stepTime = useCallback((delta) => {
    if (availableTimes.length === 0) {
      const [h, m] = onlyTime.split(":").map(Number);
      const total = h * 60 + m + delta;
      const nh = Math.floor(total / 60) % 24;
      const nm = ((total % 60) + 60) % 60;
      setOnlyTime(`${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`);
      return;
    }
    const idx = availableTimes.findIndex((t) => t.startsWith(onlyTime));
    const cur = idx >= 0 ? idx : 0;
    const next = Math.max(0, Math.min(availableTimes.length - 1, cur + delta));
    const newTime = availableTimes[next].slice(0, 5);
    setOnlyTime(newTime);
    doLoad(indexName, optionType, onlyDate, newTime, isExpiry);
  }, [availableTimes, onlyTime, doLoad, indexName, optionType, onlyDate, isExpiry]);

  // ── Derived state ────────────────────────────────────────────
  const exitClasses    = snapshotData?.exitClasses || [];
  const groupedMetrics = snapshotData?.exitRuleDataGrouped || {};
  const summary        = snapshotData?.summary || null;
  const source         = snapshotData?.source || null;

  const selectedClassData = selectedClass
    ? exitClasses.find((c) => c.className === selectedClass)
    : null;

  const matchedExitsToShow = selectedClassData?.matchedExits || [];

  return (
    <div className="es-root">

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="es-filters">

        <label>Index</label>
        <select value={indexName} onChange={(e) => setIndexName(e.target.value)}>
          {INDICES.map((i) => <option key={i}>{i}</option>)}
        </select>

        <label>Type</label>
        <select value={optionType} onChange={(e) => setOptionType(e.target.value)}>
          {OPTION_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>

        <div className="es-filter-sep" />

        {/* Expiry / Non-Expiry toggle */}
        <div className="es-mode-toggle">
          <button
            className={`es-mode-btn${isExpiry ? " es-mode-btn--active" : ""}`}
            onClick={() => setIsExpiry(true)}
          >Expiry</button>
          <button
            className={`es-mode-btn${!isExpiry ? " es-mode-btn--active es-mode-btn--nex" : ""}`}
            onClick={() => setIsExpiry(false)}
          >Non-Expiry</button>
        </div>

        {/* Opening type dropdown (non-expiry only) */}
        <select
          className={`es-opening-select${!isExpiry && !openingType ? " es-opening-select--required" : ""}`}
          value={openingType}
          onChange={(e) => setOpeningType(e.target.value)}
          disabled={isExpiry}
        >
          <option value="">-- Opening Type --</option>
          {OPENING_TYPES.map((ot) => (
            <option key={ot.value} value={ot.value}>{ot.value} - {ot.label}</option>
          ))}
        </select>

        <div className="es-filter-sep" />

        {/* Date from dateList */}
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepDate(7)} title="-7 dates">-7</button>
          <button className="es-btn" onClick={() => stepDate(1)} title="-1 date">-1</button>
        </div>
        {dateList.length > 0 ? (
          <select
            className="es-date-select"
            value={onlyDate}
            onChange={(e) => setOnlyDate(e.target.value)}
          >
            {dateList.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        ) : (
          <span className="es-date-display">
            {dateListLoading ? "Loading..." : (!isExpiry && !openingType ? "Select opening type" : onlyDate)}
          </span>
        )}
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepDate(-1)} title="+1 date">+1</button>
          <button className="es-btn" onClick={() => stepDate(-7)} title="+7 dates">+7</button>
        </div>

        <div className="es-filter-sep" />

        {/* Time navigation */}
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepTime(-5)}>-5m</button>
          <button className="es-btn" onClick={() => stepTime(-1)}>-1m</button>
        </div>
        <span className="es-time-display">{onlyTime}</span>
        <div className="es-btn-group">
          <button className="es-btn" onClick={() => stepTime(1)}>+1m</button>
          <button className="es-btn" onClick={() => stepTime(5)}>+5m</button>
        </div>

        {/* Available times dropdown */}
        {availableTimes.length > 0 && (
          <select
            value={availableTimes.find((t) => t.startsWith(onlyTime)) || ""}
            onChange={(e) => {
              const t = e.target.value.slice(0, 5);
              setOnlyTime(t);
              doLoad(indexName, optionType, onlyDate, t, isExpiry);
            }}
            style={{ width: 100 }}
          >
            {availableTimes.map((t) => (
              <option key={t} value={t}>{t.slice(0, 5)}</option>
            ))}
          </select>
        )}

        <div className="es-filter-sep" />

        <button className="es-btn es-btn--load" onClick={loadSnapshot}>
          Load
        </button>
        <button
          className="es-btn"
          style={{ background: "#3d2e00", color: "#d29922", borderColor: "#d29922" }}
          disabled={!onlyDate || !indexName || loading}
          onClick={(e) => {
            if (!onlyDate || !indexName) return;
            const btn = e.currentTarget;
            btn.textContent = "Generating...";
            btn.disabled = true;
            generateSnapshots({ indexName, onlyDate })
              .then((r) => {
                btn.textContent = `Done (${r?.timeCount || 0} min)`;
                // Reload available times after generation
                fetchAvailableTimes({ indexName, optionType, onlyDate, isExpiry })
                  .then((t) => {
                    const times = t?.times || [];
                    setAvailableTimes(times);
                    if (times.length > 0) {
                      const first = times[0].slice(0, 5);
                      setOnlyTime(first);
                      doLoad(indexName, optionType, onlyDate, first, isExpiry);
                    }
                  });
                setTimeout(() => { btn.textContent = "Generate"; btn.disabled = false; }, 3000);
              })
              .catch((err) => {
                btn.textContent = "Failed";
                console.error("Generate failed:", err);
                setTimeout(() => { btn.textContent = "Generate"; btn.disabled = false; }, 3000);
              });
          }}
        >
          Generate
        </button>

        {summary && (
          <>
            <span className="es-summary-pill es-summary-pill--matched">
              {summary.matchedClasses}/{summary.totalClasses} classes matched
            </span>
            {summary.indexOpeningType && (
              <span className="es-summary-pill es-summary-pill--opening">
                {summary.indexOpeningType}
              </span>
            )}
          </>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="es-body">

        <ExitClassList
          exitClasses={exitClasses}
          selectedClass={selectedClass}
          onSelect={setSelectedClass}
          isExpiry={isExpiry}
          openingType={openingType}
        />

        <div className="es-detail">

          {/* ── Exit Detail View (when any exit class selected) ──── */}
          {selectedClass ? (
            <B10CEDetailView
              indexName={indexName}
              optionType={optionType}
              onlyDate={onlyDate}
              onlyTime={onlyTime}
              selectedClass={selectedClass}
              isExpiry={isExpiry}
              onTimeChange={(t) => {
                setOnlyTime(t);
                doLoad(indexName, optionType, onlyDate, t, isExpiry);
              }}
              onDateChange={(d) => {
                setOnlyDate(d);
              }}
            />
          ) : (
            <>
              {loading && (
                <div className="es-loading">
                  <div className="es-spinner" />
                  Loading snapshot...
                </div>
              )}

              {!loading && error && (
                <div className="es-no-snapshot">
                  <div style={{ fontSize: 32 }}>No Data</div>
                  <div style={{ color: "#f85149" }}>{error}</div>
                  {availableTimes.length > 0 && (
                    <div style={{ color: "#8b949e", fontSize: 11 }}>
                      Available times for this date: {availableTimes.map(t => t.slice(0,5)).join(", ")}
                    </div>
                  )}
                </div>
              )}

              {!loading && !error && !snapshotData && !initialLoad && (
                <div className="es-empty">
                  <div className="es-empty__icon">Exit Analyzer</div>
                  <div>
                    {!isExpiry && !openingType
                      ? "Select an opening type (SW / GU / GD) to begin."
                      : dateList.length === 0
                        ? "No trade dates found. Try a different index or opening type."
                        : availableTimes.length === 0
                          ? "No exit data for this date."
                          : "Select a time and click Load."
                    }
                  </div>
                </div>
              )}

              {!loading && !error && !snapshotData && initialLoad && (
                <div className="es-loading">
                  <div className="es-spinner" />
                  Initializing...
                </div>
              )}

              {!loading && !error && snapshotData && (
                <>
                  <DetailHeader selectedClassData={selectedClassData} summary={summary} source={source} />

                  {selectedClassData && (
                    <MatchedExitsList matchedExits={matchedExitsToShow} />
                  )}

                  {!selectedClass && exitClasses.length > 0 && (
                    <MatchedExitsList
                      matchedExits={exitClasses.flatMap((c) => c.matchedExits || [])}
                    />
                  )}

                  <div className="es-metrics-scroll">
                    {Object.keys(groupedMetrics).length === 0 ? (
                      <div className="es-no-snapshot">
                        <div>No metric data available for this snapshot.</div>
                      </div>
                    ) : (
                      <div className="es-metric-groups">
                        {Object.entries(groupedMetrics).map(([groupName, fields]) => (
                          <MetricGroup key={groupName} name={groupName} fields={fields} />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
